(ns dnd.drag-and-drop.core
  (:require [och-graph.core :as g]
            ["transformation-matrix" :as matrix]
            [re-frame.core :refer [console]]))

;;;; ========== Drop position computation ==========
;;
;; Each position represents a legal place the dragged node could go.
;; Instead of rectangular zones, each position has a single representative
;; point in SVG space. Hit-testing finds the nearest point with hysteresis.
;;
;; Position shape:
;;   {:position-id  string
;;    :point        {:x :y}
;;    :action       {:type :target-node-id ...}}

(defn sorted-children
  [{:keys [graph parent-node-id]}]
  (let [child-ids (g/children-ids graph parent-node-id)]
    (->> child-ids
         (map #(g/get-node graph %))
         (sort-by :position))))

(defn leaf-node?
  [{:keys [graph node-id]}]
  (empty? (g/children-ids graph node-id)))

(defn layout-center-x
  [{:keys [layout]}]
  (+ (:x layout) (/ (:width layout) 2)))

(defn layout-center-y
  [{:keys [layout]}]
  (+ (:y layout) (/ (:height layout) 2)))

(defn view-id->layout
  [{:keys [view-layouts node-id]}]
  (get view-layouts (str "flex-view-" node-id)))

(defn compute-drop-positions
  [{:keys [view-layouts graph gap-bottom]
    :or {gap-bottom 160}}]
  (let [all-nodes (:nodes graph)
        positions (transient [])]
    (doseq [node all-nodes]
      (let [node-id (:id node)
            layout (view-id->layout {:view-layouts view-layouts :node-id node-id})]
        (when (and layout
                   (pos? (:width layout 0))
                   (pos? (:height layout 0))
                   (not= (:type node) "PhantomNode"))
          (let [parent-node-id (g/parent-id graph node-id)
                is-leaf (leaf-node? {:graph graph :node-id node-id})
                siblings (sorted-children {:graph graph :parent-node-id parent-node-id})
                is-leftmost (= node-id (:id (first siblings)))
                is-rightmost (= node-id (:id (last siblings)))
                cx (layout-center-x {:layout layout})
                cy (layout-center-y {:layout layout})]

            ;; reparent-under: point below a leaf node
            (when is-leaf
              (conj! positions {:position-id (str "reparent-under-" node-id)
                                :point {:x cx
                                        :y (+ (:y layout) (:height layout) (/ gap-bottom 2))}
                                :action {:type :reparent-under
                                         :target-node-id node-id}}))

            ;; insert-above: point above any node (become its manager)
            (conj! positions {:position-id (str "insert-above-" node-id)
                              :point {:x cx
                                      :y (- (:y layout) (/ gap-bottom 2))}
                              :action {:type :insert-above
                                       :target-node-id node-id}})

            ;; insert-before leftmost sibling: point to its left
            (when is-leftmost
              (conj! positions {:position-id (str "insert-before-" node-id)
                                :point {:x (- (:x layout) 60)
                                        :y cy}
                                :action {:type :insert-before
                                         :target-node-id node-id}}))

            ;; insert-after rightmost sibling: point to its right
            (when is-rightmost
              (conj! positions {:position-id (str "insert-after-" node-id)
                                :point {:x (+ (:x layout) (:width layout) 60)
                                        :y cy}
                                :action {:type :insert-after
                                         :target-node-id node-id}}))

            ;; insert-before right sibling: point in the gap between this and right neighbor
            (when (and (not is-rightmost) (> (count siblings) 1))
              (let [sorted-sibs (vec siblings)
                    idx (some (fn [[i s]] (when (= (:id s) node-id) i))
                              (map-indexed vector sorted-sibs))]
                (when (and idx (< (inc idx) (count sorted-sibs)))
                  (let [right-sibling (nth sorted-sibs (inc idx))
                        right-layout (view-id->layout {:view-layouts view-layouts
                                                       :node-id (:id right-sibling)})]
                    (when right-layout
                      (let [gap-x (/ (+ (:x layout) (:width layout)
                                        (:x right-layout))
                                     2)
                            gap-y (/ (+ cy (layout-center-y {:layout right-layout})) 2)]
                        (conj! positions {:position-id (str "insert-after-" node-id)
                                          :point {:x gap-x :y gap-y}
                                          :action {:type :insert-after
                                                   :target-node-id node-id}})))))))))))
    (persistent! positions)))

;;;; ========== Phantom candidate point ==========
;;
;; When a phantom node is placed in the layout, its center becomes
;; a candidate position with the same action as the current hovered
;; position. This prevents oscillation: the phantom is near the cursor,
;; so it stays selected even after layout shifts move the original
;; position point.

(defn add-phantom-candidate
  [{:keys [positions view-layouts hovered-position]}]
  (if-not hovered-position
    positions
    (let [phantom-layout (get view-layouts "flex-view-phantom-drop-target")]
      (if-not phantom-layout
        positions
        (let [cx (+ (:x phantom-layout) (/ (:width phantom-layout) 2))
              cy (+ (:y phantom-layout) (/ (:height phantom-layout) 2))]
          (conj positions {:position-id (str "phantom-" (:position-id hovered-position))
                           :point {:x cx :y cy}
                           :action (:action hovered-position)}))))))

;;;; ========== Coordinate transform + hit testing ==========

(defn screen->svg-point
  [{:keys [transform-matrix screen-x screen-y]}]
  (let [inv (matrix/inverse (clj->js transform-matrix))
        pt (matrix/applyToPoint inv #js {:x screen-x :y screen-y})]
    {:x (.-x pt)
     :y (.-y pt)}))

(def max-snap-distance 200)
(def default-hysteresis 50)

(defn distance-to-point
  [{x1 :x y1 :y} {x2 :x y2 :y}]
  (js/Math.sqrt (+ (* (- x2 x1) (- x2 x1))
                   (* (- y2 y1) (- y2 y1)))))

(defn rect-corners
  [{:keys [x y width height]}]
  [{:x x :y y}
   {:x (+ x width) :y y}
   {:x (+ x width) :y (+ y height)}
   {:x x :y (+ y height)}])

(defn polygon-from-point-and-rect
  "Convex hull of a point + 4 rect corners, sorted CCW by atan2 from centroid."
  [{:keys [point rect]}]
  (let [corners (rect-corners rect)
        all-pts (conj corners point)
        n (count all-pts)
        cx (/ (reduce + (map :x all-pts)) n)
        cy (/ (reduce + (map :y all-pts)) n)]
    (vec (sort-by (fn [{:keys [x y]}]
                    (js/Math.atan2 (- y cy) (- x cx)))
                  all-pts))))

(defn point-in-polygon?
  "Cross-product sign test for CCW-wound convex polygon.
   Returns true if point is inside or on the boundary."
  [{:keys [point polygon]}]
  (let [n (count polygon)]
    (every? (fn [i]
              (let [a (nth polygon i)
                    b (nth polygon (mod (inc i) n))
                    ;; cross product of edge vector (a->b) and point vector (a->point)
                    cross (- (* (- (:x b) (:x a)) (- (:y point) (:y a)))
                             (* (- (:y b) (:y a)) (- (:x point) (:x a))))]
                (>= cross 0)))
            (range n))))

(defn convex-hull-from-points
  "Sort points CCW by atan2 from centroid. Works for any set of points
   whose convex hull we want (assumes all points are on the hull)."
  [points]
  (when (seq points)
    (let [n (count points)
          cx (/ (reduce + (map :x points)) n)
          cy (/ (reduce + (map :y points)) n)]
      (vec (sort-by (fn [{:keys [x y]}]
                      (js/Math.atan2 (- y cy) (- x cx)))
                    points)))))

(defn polygon-from-two-rects
  "Convex hull of two rects' corners, sorted CCW."
  [{:keys [rect-a rect-b]}]
  (when (and rect-a rect-b)
    (convex-hull-from-points (into (rect-corners rect-a)
                                   (rect-corners rect-b)))))

(defn compute-safe-zone
  "Build safe zone polygon. For reparent-under positions, the safe zone
   covers the target card rect to the phantom rect (so moving between
   the card and phantom is protected). For other positions, it's the
   lock-in point to phantom corridor."
  [{:keys [lock-in-svg-point phantom-layout target-card-layout]}]
  (cond
    ;; Reparent-under: corridor from target card to phantom
    (and target-card-layout phantom-layout)
    (polygon-from-two-rects {:rect-a target-card-layout
                             :rect-b phantom-layout})

    ;; Other positions: corridor from lock-in point to phantom
    (and lock-in-svg-point phantom-layout)
    (polygon-from-point-and-rect {:point lock-in-svg-point
                                  :rect phantom-layout})))

(defn point-in-rect?
  [{:keys [point rect]}]
  (boolean (and rect
                (>= (:x point) (:x rect))
                (<= (:x point) (+ (:x rect) (:width rect)))
                (>= (:y point) (:y rect))
                (<= (:y point) (+ (:y rect) (:height rect))))))

(defn cursor-in-safe-zone?
  [{:keys [svg-point safe-zone phantom-layout target-card-layout]}]
  (boolean (or (point-in-rect? {:point svg-point :rect phantom-layout})
               (point-in-rect? {:point svg-point :rect target-card-layout})
               (and safe-zone
                    (point-in-polygon? {:point svg-point :polygon safe-zone})))))

(defn find-nearest-position
  "Find the nearest position to svg-point within max-snap-distance.
   Pure function — no hysteresis logic here. Hysteresis is handled
   by the FSM via lock-in-svg-point."
  [{:keys [positions svg-point]}]
  (let [nearest (when (seq positions)
                  (reduce (fn [best pos]
                            (let [dist (distance-to-point svg-point (:point pos))]
                              (if (or (nil? best) (< dist (:dist best)))
                                {:position pos :dist dist}
                                best)))
                          nil
                          positions))]
    (when (and nearest (< (:dist nearest) max-snap-distance))
      (:position nearest))))

(defn find-card-under-point
  "If the hit-point is inside a card's bounding rect, return a
   :reparent-under position for that card. This gives 'drop on card'
   priority over nearest-gap positions. Returns nil if the point
   isn't over any card."
  [{:keys [svg-point view-layouts graph dragged-node-id]}]
  (let [nodes (:nodes graph)]
    (some (fn [node]
            (let [node-id (:id node)
                  layout (get view-layouts (str "flex-view-" node-id))]
              (when (and layout
                         (not= node-id dragged-node-id)
                         (not= node-id "phantom-drop-target")
                         (not= (:type node) "PhantomNode")
                         (not (g/is-section-node? node))
                         (point-in-rect? {:point svg-point :rect layout}))
                {:position-id (str "reparent-onto-" node-id)
                 :point {:x (+ (:x layout) (/ (:width layout) 2))
                         :y (+ (:y layout) (/ (:height layout) 2))}
                 :action {:type :reparent-under
                          :target-node-id node-id}})))
          nodes)))

(defn would-place-in-section
  "Given a drop action and graph, return the section-id the node would
   end up in after the drop (nil = unsectioned)."
  [{:keys [action graph]}]
  (let [{:keys [type target-node-id]} action
        parent-after-drop (case type
                            :reparent-under target-node-id
                            :insert-above (g/parent-id graph target-node-id)
                            :insert-before (g/parent-id graph target-node-id)
                            :insert-after (g/parent-id graph target-node-id)
                            nil)]
    (when parent-after-drop
      (let [parent-node (g/get-node graph parent-after-drop)]
        (if (g/is-section-node? parent-node)
          parent-after-drop
          (g/containing-section-id graph parent-after-drop))))))

(defn filter-positions-for-sections
  "Remove positions that violate section rules. Works for both chart
   drags and sidebar drags (no dragged-node-id required).
   Optional :contact-vid to check cross-section duplicates.
   Optional :current-section-id for the dragged node's current section."
  [{:keys [positions graph contact-vid current-section-id]}]
  (let [has-sections? (g/has-sections? graph)]
    (filterv
      (fn [position]
        (let [{:keys [type target-node-id]} (:action position)
              target-node (g/get-node graph target-node-id)]
          (and
            ;; Can't make a section node non-root (insert-above would
            ;; reparent it under the dragged node). reparent-under is
            ;; fine — it places the node as a child of the section.
            ;; insert-before/after a section node is fine too — it
            ;; places the dragged node as a root-level sibling.
            (not (and (g/is-section-node? target-node)
                      (= type :insert-above)))
            ;; Don't place a contact into a section where it already exists.
            ;; nil represents the "unsectioned" virtual section.
            (not (and has-sections?
                      contact-vid
                      (let [dest-section (would-place-in-section {:action (:action position)
                                                                  :graph graph})]
                        (and (not= dest-section current-section-id)
                             (let [all-nodes-for-vid (g/get-nodes-by-vid graph contact-vid)
                                   existing-sections (set (map (fn [n]
                                                                 (g/containing-section-id graph (:id n)))
                                                               all-nodes-for-vid))]
                               (contains? existing-sections dest-section)))))))))
      positions)))

(defn filter-noop-positions
  "Remove positions that are no-ops for an existing chart node.
   Only applies to chart drags (sidebar drags add new nodes)."
  [{:keys [positions graph dragged-node-id]}]
  (let [dragged-parent-id (g/parent-id graph dragged-node-id)]
    (filterv
      (fn [position]
        (let [{:keys [type target-node-id]} (:action position)]
          (and
            ;; Not targeting self
            (not= target-node-id dragged-node-id)
            ;; insert-above a direct child is a no-op (already the parent)
            (not (and (= type :insert-above)
                      (= (g/parent-id graph target-node-id) dragged-node-id)))
            ;; Not a no-op reparent (already child of that parent)
            (not
              (and (= type :reparent-under)
                   (= target-node-id dragged-parent-id)))
            ;; Not a no-op sibling reposition
            (not
              (and (contains? #{:insert-before :insert-after} type)
                   (let [target-parent (g/parent-id graph target-node-id)]
                     (and (= target-parent dragged-parent-id)
                          (let [siblings (sorted-children {:graph graph :parent-node-id dragged-parent-id})
                                sibling-ids (mapv :id siblings)
                                dragged-idx (some (fn [[i id]] (when (= id dragged-node-id) i))
                                                  (map-indexed vector sibling-ids))
                                target-idx (some (fn [[i id]] (when (= id target-node-id) i))
                                                 (map-indexed vector sibling-ids))]
                            (cond
                              (= type :insert-before)
                              (or (= target-idx dragged-idx)
                                  (= target-idx (inc dragged-idx)))

                              (= type :insert-after)
                              (or (= target-idx dragged-idx)
                                  (= target-idx (dec dragged-idx)))

                              :else false)))))))))
      positions)))

(defn filter-valid-positions
  "Given all candidate positions, remove those that are invalid for the
   item being placed.

   drag-subject identifies what is being dragged:
     {:type :node        :id \"node-id\"}  — existing chart node being moved
     {:type :contact     :id 1234}        — contact being added from sidebar
     {:type :placeholder}                 — placeholder being added from sidebar"
  [{:keys [positions graph drag-subject]}]
  (assert (#{:node :contact :placeholder} (:type drag-subject))
          "drag-subject :type must be :node, :contact, or :placeholder")
  (assert (or (= (:type drag-subject) :placeholder)
              (some? (:id drag-subject)))
          "drag-subject must have :id (unless :placeholder)")
  (let [node-id (when (= (:type drag-subject) :node)
                  (:id drag-subject))
        contact-vid (case (:type drag-subject)
                      :node (:contact-vid (g/get-node graph node-id))
                      :contact (:id drag-subject)
                      :placeholder nil)
        ;; For chart drags the contact is "coming from" its current section,
        ;; so repositioning within that section is allowed.
        ;; For sidebar drags the contact isn't coming from anywhere — use a
        ;; sentinel so it never matches a real section (including unsectioned/nil).
        source-section-id (if node-id
                            (g/containing-section-id graph node-id)
                            ::no-section)
        section-filtered (filter-positions-for-sections
                           {:positions positions
                            :graph graph
                            :contact-vid contact-vid
                            :current-section-id source-section-id})]
    (if node-id
      (filter-noop-positions
        {:positions section-filtered
         :graph graph
         :dragged-node-id node-id})
      section-filtered)))

;;;; ========== Branch layers from node-defs ==========
;;
;; Derives the same shape as :sub.current-chart/reporting-line-branch-layers
;; but from node-defs instead of the graph. Used to show preview reporting
;; lines during drag.

(defn node-defs->branch-layers
  [node-defs]
  (let [by-parent (group-by :parent-node-id node-defs)
        node-type-map (reduce (fn [acc nd]
                                (assoc acc (get-in nd [:node :id])
                                           (get-in nd [:node :type])))
                              {}
                              node-defs)]
    (->> (dissoc by-parent nil)
         (keep (fn [[parent-id children]]
                 (let [parent-type (get node-type-map parent-id)]
                   (when (not= parent-type "SectionNode")
                     {:node-id parent-id
                      :node-type parent-type
                      :children (->> children
                                     (sort-by #(get-in % [:node :position]))
                                     (mapv (fn [child]
                                             {:node-id (get-in child [:node :id])
                                              :node-type (get-in child [:node :type])})))}))))
         (vec))))

;;;; ========== Preview reporting lines ==========
;;
;; Pure computation of reporting line SVG paths from view-layouts
;; and branch layers. Mirrors the logic in ui-reporting-line-layer
;; but as data, not React components.

(defn layout-mid-x
  [view-layouts node-id]
  (let [layout (get view-layouts node-id)]
    (when layout
      (+ (:x layout) (/ (:width layout) 2)))))

(defn compute-layer-paths
  "Compute SVG path strings for one branch layer (parent + children).
   Returns {:key :paths [{:d :spacer?}]}"
  [{:keys [view-layouts layer]}]
  (let [parent-node-id (:node-id layer)
        parent-type (:node-type layer)
        children (:children layer)
        first-child (first children)
        last-child (last children)
        first-child-node-id (:node-id first-child)
        last-child-node-id (:node-id last-child)
        last-reporting-child-node-id (->> children
                                          (filterv #(not= (:node-type %) "SpacerNode"))
                                          last
                                          :node-id)
        parent-layout (get view-layouts parent-node-id)
        first-child-layout (get view-layouts first-child-node-id)]
    (when (and parent-layout first-child-layout)
      (let [parent-mid-x (+ (:x parent-layout) (/ (:width parent-layout) 2))
            horizontal-y-min (+ (:y parent-layout) (:height parent-layout))
            horizontal-y-max (:y first-child-layout)
            horizontal-y (/ (+ horizontal-y-min horizontal-y-max) 2)]
        (if (= (count children) 1)
          {:key parent-node-id
           :paths [{:d (str "M" parent-mid-x "," horizontal-y-min
                            " V" horizontal-y-max)
                    :spacer? (or (= parent-type "SpacerNode")
                                 (= (:node-type first-child) "SpacerNode"))}]}
          (let [spacer-node-id (when (= (:node-type last-child) "SpacerNode")
                                 last-child-node-id)
                horizontal-x-min (layout-mid-x view-layouts first-child-node-id)
                horizontal-x-max (layout-mid-x view-layouts last-child-node-id)
                horizontal-reporting-x-max (js/Math.max
                                             (or (layout-mid-x view-layouts last-reporting-child-node-id)
                                                 parent-mid-x)
                                             parent-mid-x)
                child-vertical-paths (->> children
                                          (remove (fn [{:keys [node-id]}]
                                                    (or (= node-id first-child-node-id)
                                                        (= node-id last-child-node-id)
                                                        (and spacer-node-id
                                                             (= node-id spacer-node-id)))))
                                          (keep (fn [{:keys [node-id]}]
                                                  (let [layout (get view-layouts node-id)]
                                                    (when layout
                                                      {:d (str "M" (+ (:x layout) (/ (:width layout) 2))
                                                               "," horizontal-y
                                                               " V" (:y layout))
                                                       :spacer? false})))))
                parent-vertical {:d (str "M" parent-mid-x "," horizontal-y
                                         " V" horizontal-y-min)
                                 :spacer? false}
                handlebar {:d (str "M" horizontal-x-min "," horizontal-y-max
                                   " V" horizontal-y
                                   " H" horizontal-reporting-x-max
                                   (when-not spacer-node-id
                                     (str " V" horizontal-y-max)))
                           :spacer? false}
                spacer-handlebar (when spacer-node-id
                                   {:d (str "M" horizontal-reporting-x-max "," horizontal-y
                                            " H" horizontal-x-max
                                            " V" horizontal-y-max)
                                    :spacer? true})]
            {:key parent-node-id
             :paths (cond-> [handlebar parent-vertical]
                            spacer-handlebar (conj spacer-handlebar)
                            true (into child-vertical-paths))}))))))

(defn compute-reporting-lines
  [{:keys [view-layouts branch-layers]}]
  (->> branch-layers
       (keep (fn [layer]
               (compute-layer-paths {:view-layouts view-layouts
                                     :layer layer})))
       (into [])))

;;;; ========== Phantom/preview node-defs ==========

(def fixed-phantom-size {:width 340 :height 90})
(def sidebar-drag-phantom-size {:width 340 :height 90})

(defn compute-preview-node-defs
  [{:keys [node-defs
           dragged-node-id
           hovered-position
           remove-original?
           fixed-phantom-size?
           sidebar-drag?]}]
  (if-not hovered-position
    node-defs
    (let [{:keys [type target-node-id]} (:action hovered-position)
          target-node-def (some #(when (= (get-in % [:node :id]) target-node-id) %) node-defs)
          target-parent-id (:parent-node-id target-node-def)
          target-position (get-in target-node-def [:node :position])
          dragged-node-def (some #(when (= (get-in % [:node :id]) dragged-node-id) %) node-defs)
          dragged-parent-id (:parent-node-id dragged-node-def)
          phantom-width (cond
                          sidebar-drag? (:width sidebar-drag-phantom-size)
                          fixed-phantom-size? (:width fixed-phantom-size)
                          :else (get-in dragged-node-def [:node :metadata :width] 200))
          phantom-height (cond
                           sidebar-drag? (:height sidebar-drag-phantom-size)
                           fixed-phantom-size? (:height fixed-phantom-size)
                           :else (get-in dragged-node-def [:node :metadata :height] 100))
          ;; Either remove dragged node + reparent children, or keep everything
          base-defs (if remove-original?
                      (reduce
                        (fn [acc nd]
                          (cond
                            (= (get-in nd [:node :id]) dragged-node-id)
                            acc

                            (= (:parent-node-id nd) dragged-node-id)
                            (conj acc (assoc nd :parent-node-id dragged-parent-id))

                            :else
                            (conj acc nd)))
                        []
                        node-defs)
                      node-defs)
          phantom-node {:id "phantom-drop-target"
                        :type "PhantomNode"
                        :position 0
                        :metadata {:width phantom-width
                                   :height phantom-height
                                   :position-animation? false}}
          target-is-section? (= (get-in target-node-def [:node :type]) "SectionNode")
          phantom-def (fn [parent-id position {:keys [unsectioned?]}]
                        {:id "phantom-drop-target"
                         :node (assoc phantom-node :position position)
                         :parent-node-id parent-id
                         :section-id (when-not unsectioned?
                                       (:section-id target-node-def))
                         :section-position (when-not unsectioned?
                                             (:section-position target-node-def))})
          compact-sibling-positions (fn [defs parent-id]
                                      (let [siblings (->> defs
                                                          (filter #(= (:parent-node-id %) parent-id))
                                                          (sort-by #(get-in % [:node :position])))
                                            position-map (into {}
                                                               (map-indexed (fn [i nd]
                                                                              [(get-in nd [:node :id]) i])
                                                                            siblings))]
                                        (mapv (fn [nd]
                                                (if-let [new-pos (get position-map (get-in nd [:node :id]))]
                                                  (assoc-in nd [:node :position] new-pos)
                                                  nd))
                                              defs)))]
      (case type
        :reparent-under
        (let [existing-children (->> base-defs
                                     (filter #(= (:parent-node-id %) target-node-id)))
              n (count existing-children)
              mid-pos (/ n 2)]
          (-> base-defs
              (conj (phantom-def target-node-id (- mid-pos 0.5) nil))
              (compact-sibling-positions target-node-id)))

        :insert-above
        (-> base-defs
            (conj (phantom-def target-parent-id
                               (if target-position (- target-position 0.5) -1)
                               nil))
            (->> (mapv (fn [nd]
                         (if (= (get-in nd [:node :id]) target-node-id)
                           (assoc nd :parent-node-id "phantom-drop-target")
                           nd))))
            (compact-sibling-positions target-parent-id))

        :insert-before
        (-> base-defs
            (conj (phantom-def target-parent-id
                               (if target-position (- target-position 0.5) -1)
                               {:unsectioned? target-is-section?}))
            (compact-sibling-positions target-parent-id))

        :insert-after
        (-> base-defs
            (conj (phantom-def target-parent-id
                               (if target-position (+ target-position 0.5) 999)
                               {:unsectioned? target-is-section?}))
            (compact-sibling-positions target-parent-id))

        node-defs))))

;;;; ========== Graph mutations ==========

(defn set-node-position
  [graph {:keys [node-id position]}]
  (update graph :nodes
          (fn [nodes]
            (mapv (fn [n]
                    (if (= (:id n) node-id)
                      (assoc n :position position)
                      n))
                  nodes))))

(defn detach-node
  "Reparent node's children to node's parent, then reparent node to new-parent.
   The node moves alone — descendants stay behind, slotting into the
   position the dragged node occupied among its siblings."
  [graph {:keys [node-id new-parent-id]}]
  (let [old-parent-id (g/parent-id graph node-id)
        dragged-pos (:position (g/get-node graph node-id) 0)
        child-ids (g/children-ids graph node-id)
        sorted-child-ids (->> child-ids
                              (map #(g/get-node graph %))
                              (sort-by :position)
                              (mapv :id))
        n (count sorted-child-ids)
        ;; Reparent children to grandparent
        graph-with-children-moved (reduce
                                    (fn [g child-id]
                                      (g/reparent-node g child-id old-parent-id))
                                    graph
                                    sorted-child-ids)
        ;; Assign fractional positions so children slot in where the
        ;; dragged node was, preserving their relative order.
        ;; Uses dragged-pos + i/(n+1) so all values fall between
        ;; dragged-pos and dragged-pos+1 (the next sibling slot).
        ;; compact-children-positions normalizes to integers afterward.
        graph-with-positions (reduce
                               (fn [g [i child-id]]
                                 (set-node-position g {:node-id child-id
                                                       :position (+ dragged-pos (/ (inc i) (inc n)))}))
                               graph-with-children-moved
                               (map-indexed vector sorted-child-ids))
        graph-compacted (g/compact-children-positions graph-with-positions old-parent-id)]
    (g/reparent-node graph-compacted node-id new-parent-id)))

(defn move-node
  "Reparent node to new-parent, keeping all descendants.
   Compacts the old parent's children after the move."
  [graph {:keys [node-id new-parent-id]}]
  (let [old-parent-id (g/parent-id graph node-id)
        moved (g/reparent-node graph node-id new-parent-id)]
    (if old-parent-id
      (g/compact-children-positions moved old-parent-id)
      moved)))

(defn apply-drop-action
  [{:keys [graph dragged-node-id action keep-descendants?]}]
  (let [{:keys [type target-node-id]} action
        relocate (if keep-descendants? move-node detach-node)]
    (case type
      :reparent-under
      (let [relocated (relocate graph {:node-id dragged-node-id
                                       :new-parent-id target-node-id})
            ;; Place in the middle of the other children (exclude the
            ;; dragged node from the count so the formula matches the
            ;; preview in compute-preview-node-defs).
            existing-children (sorted-children {:graph relocated
                                                :parent-node-id target-node-id})
            n (dec (count existing-children))
            mid-pos (/ n 2)]
        (-> relocated
            (set-node-position {:node-id dragged-node-id
                                :position (- mid-pos 0.5)})
            (g/compact-children-positions target-node-id)))

      :insert-above
      (let [target-parent-id (g/parent-id graph target-node-id)
            target-node (g/get-node graph target-node-id)
            preserve-position (:position target-node 0)]
        (-> graph
            (relocate {:node-id dragged-node-id
                       :new-parent-id target-parent-id})
            (set-node-position {:node-id dragged-node-id
                                :position preserve-position})
            (g/reparent-node target-node-id dragged-node-id)
            (g/compact-children-positions target-parent-id)
            (g/compact-children-positions dragged-node-id)))

      :insert-before
      ;; Relocate first (detach/move + compact), then read target's
      ;; parent and position from the post-relocate graph. This handles
      ;; the case where the target is a direct child of the dragged node:
      ;; after detach, the target's parent changes from the dragged node
      ;; to the grandparent, avoiding a self-loop.
      (let [detached (relocate graph {:node-id dragged-node-id
                                      :new-parent-id nil})
            target-parent-id (g/parent-id detached target-node-id)
            relocated (g/reparent-node detached dragged-node-id target-parent-id)
            target-pos (:position (g/get-node relocated target-node-id) 0)]
        (-> relocated
            (set-node-position {:node-id dragged-node-id
                                :position (- target-pos 0.5)})
            (g/compact-children-positions target-parent-id)))

      :insert-after
      (let [detached (relocate graph {:node-id dragged-node-id
                                      :new-parent-id nil})
            target-parent-id (g/parent-id detached target-node-id)
            relocated (g/reparent-node detached dragged-node-id target-parent-id)
            target-pos (:position (g/get-node relocated target-node-id) 0)]
        (-> relocated
            (set-node-position {:node-id dragged-node-id
                                :position (+ target-pos 0.5)})
            (g/compact-children-positions target-parent-id)))

      graph)))

(defn apply-sidebar-drop-action
  "Place a brand-new node (already created) into the graph at the
   position described by `action`. Used for sidebar-to-chart drag."
  [{:keys [graph new-node action]}]
  (let [{:keys [type target-node-id]} action]
    (case type
      :reparent-under
      (-> graph
          (g/add-node new-node)
          (g/reparent-node (:id new-node) target-node-id)
          (g/compact-children-positions target-node-id))

      :insert-above
      (g/splice-in-node graph target-node-id new-node)

      :insert-before
      (let [target-parent-id (g/parent-id graph target-node-id)
            target-node (g/get-node graph target-node-id)
            target-pos (:position target-node 0)]
        (-> graph
            (g/add-node new-node)
            (g/reparent-node (:id new-node) target-parent-id)
            (set-node-position {:node-id (:id new-node)
                                :position (- target-pos 0.5)})
            (g/compact-children-positions target-parent-id)))

      :insert-after
      (let [target-parent-id (g/parent-id graph target-node-id)
            target-node (g/get-node graph target-node-id)
            target-pos (:position target-node 0)]
        (-> graph
            (g/add-node new-node)
            (g/reparent-node (:id new-node) target-parent-id)
            (set-node-position {:node-id (:id new-node)
                                :position (+ target-pos 0.5)})
            (g/compact-children-positions target-parent-id)))

      graph)))

(defn phantom-parent-id
  "Given an action and graph, return the parent-node-id the phantom would have.
   Used to detect when a position switch actually moves the phantom to a
   different part of the tree (vs just a different position among the same
   siblings)."
  [{:keys [action graph]}]
  (let [{:keys [type target-node-id]} action]
    (case type
      :reparent-under target-node-id
      :insert-above (g/parent-id graph target-node-id)
      :insert-before (g/parent-id graph target-node-id)
      :insert-after (g/parent-id graph target-node-id)
      nil)))

;;;; ========== Pure drag state machine ==========
;;
;; State shape:
;;   {:phase              :idle | :pending | :dragging
;;    :drag-source        :chart | :sidebar | nil
;;    :dragged-node-id    string | nil
;;    :dragged-node-size  {:width :height} | nil
;;    :dragged-node-layout map | nil          — view-layout of the dragged node at pickup
;;    :sidebar-drag-data  map | nil           — sidebar-specific data (type, vid, name, etc.)
;;    :start-mouse-pos    {:x :y} | nil
;;    :current-mouse-pos  {:x :y} | nil
;;    :grab-offset        {:x :y} | nil
;;    :hovered-position   position | nil
;;    :lock-in-svg-point  {:x :y} | nil       — cursor SVG pos when position was selected
;;    :safe-zone-polygon  [points] | nil       — convex hull for debug overlay
;;    :nil-hold?          boolean              — true when absorbing a single-frame nil
;;                                               (see nil-hold comment in :dragging :mouse-move)}
;;
;; Events:
;;   [:mouse-down         {:node-id :node-size :node-layout :screen-pos :grab-offset}]
;;   [:sidebar-mouse-down {:sidebar-drag-data :node-size :screen-pos :grab-offset}]
;;   [:mouse-move         {:screen-pos :svg-point :copy-center-svg :graph :positions :phantom-layout}]
;;   [:mouse-up           {:graph :keep-descendants?}]
;;
;; Effects (returned by step, executed by shell):
;;   :start-tracking       — attach window mousemove/mouseup listeners
;;   :stop-tracking        — remove listeners, destroy float element
;;   :drag-started         — create float element, dispatch re-frame event
;;   :drag-ended           — dispatch re-frame event
;;   :fire-click           — forward click to node (pending → idle on mouseup)
;;   [:commit-graph g]     — apply graph mutation (chart drag drop)
;;   [:commit-sidebar-drop {:graph :action :sidebar-drag-data}] — apply sidebar drop

(def initial-state
  {:phase :idle
   :drag-source nil
   :dragged-node-id nil
   :dragged-node-size nil
   :dragged-node-layout nil
   :sidebar-drag-data nil
   :start-mouse-pos nil
   :current-mouse-pos nil
   :grab-offset nil
   :hovered-position nil
   :lock-in-svg-point nil
   :safe-zone-polygon nil
   :nil-hold? false
   :shift-held? false
   :descendant-ids #{}})

(def drag-threshold 5)

(defn distance [{x1 :x y1 :y} {x2 :x y2 :y}]
  (js/Math.sqrt (+ (* (- x2 x1) (- x2 x1))
                   (* (- y2 y1) (- y2 y1)))))

(defn step
  [{:keys [state event]}]
  (let [[event-type event-data] event
        phase (:phase state)]
    (case [phase event-type]

      [:idle :mouse-down]
      (let [{:keys [node-id node-size node-layout screen-pos grab-offset]} event-data]
        {:state (assoc state
                  :phase :pending
                  :drag-source :chart
                  :dragged-node-id node-id
                  :dragged-node-size node-size
                  :dragged-node-layout node-layout
                  :start-mouse-pos screen-pos
                  :current-mouse-pos screen-pos
                  :grab-offset grab-offset
                  :hovered-position nil)
         :effects [:start-tracking]})

      [:idle :sidebar-mouse-down]
      (let [{:keys [sidebar-drag-data node-size screen-pos grab-offset]} event-data]
        {:state (assoc state
                  :phase :pending
                  :drag-source :sidebar
                  :sidebar-drag-data sidebar-drag-data
                  :dragged-node-id nil
                  :dragged-node-size node-size
                  :start-mouse-pos screen-pos
                  :current-mouse-pos screen-pos
                  :grab-offset grab-offset
                  :hovered-position nil)
         :effects [:start-tracking]})

      [:pending :mouse-move]
      (let [{:keys [screen-pos graph shift-held?]} event-data
            dist (distance (:start-mouse-pos state) screen-pos)]
        (if (> dist drag-threshold)
          (let [dragged-node-id (:dragged-node-id state)
                descendant-ids (if (and graph dragged-node-id)
                                 (g/descendant-ids graph dragged-node-id)
                                 #{})]
            {:state (assoc state
                      :phase :dragging
                      :current-mouse-pos screen-pos
                      :shift-held? (boolean shift-held?)
                      :descendant-ids descendant-ids)
             :effects [:drag-started]})
          {:state state}))

      [:pending :mouse-up]
      (if (= (:drag-source state) :sidebar)
        (do
          (console :debug "[drag] pending -> idle (sidebar cancel)")
          {:state initial-state
           :effects [:stop-tracking]})
        (do
          (console :debug "[drag] pending -> idle (click-through)" (:dragged-node-id state))
          {:state initial-state
           :effects [:stop-tracking :fire-click]}))

      [:dragging :mouse-move]
      (let [{:keys [screen-pos
                    svg-point
                    copy-center-svg
                    graph
                    positions
                    view-layouts
                    phantom-layout
                    enable-card-under?
                    shift-held?]} event-data
            ;; Use copy center for hit-testing when available, fall back to cursor
            hit-point (or copy-center-svg svg-point)
            dragged-node-id (:dragged-node-id state)
            drag-subject (if (= (:drag-source state) :sidebar)
                           (let [{:keys [type vid]} (:sidebar-drag-data state)]
                             (case type
                               :contact {:type :contact :id vid}
                               :placeholder {:type :placeholder}))
                           {:type :node
                            :id dragged-node-id})
            filtered (when (and graph positions hit-point)
                       (filter-valid-positions
                         {:positions positions
                          :graph graph
                          :drag-subject drag-subject}))
            current-pos (:hovered-position state)
            lock-in (:lock-in-svg-point state)
            ;; Two checks gate position switching (based on cursor, not copy center):
            ;; 1. Cursor must escape the hysteresis circle
            outside-hysteresis? (or (nil? lock-in)
                                    (nil? current-pos)
                                    (> (distance-to-point svg-point lock-in) default-hysteresis))
            ;; 2. Cursor must also escape the safe zone polygon.
            ;; For reparent-under, the corridor covers the target card
            ;; to the phantom (so moving between card and phantom is safe).
            ;; For other positions, it's lock-in point to phantom.
            target-card-layout (when (and current-pos view-layouts
                                          (= :reparent-under (get-in current-pos [:action :type])))
                                 (get view-layouts
                                      (str "flex-view-" (get-in current-pos [:action :target-node-id]))))
            safe-zone (when (and lock-in current-pos)
                        (compute-safe-zone {:lock-in-svg-point lock-in
                                            :phantom-layout phantom-layout
                                            :target-card-layout target-card-layout}))
            inside-safe-zone? (cursor-in-safe-zone? {:svg-point svg-point
                                                     :safe-zone safe-zone
                                                     :phantom-layout phantom-layout
                                                     :target-card-layout target-card-layout})
            cursor-moved-enough? (and outside-hysteresis? (not inside-safe-zone?))
            ;; "Drop on card" override: if the copy center is inside a
            ;; card's rect, treat it as reparent-under that card. Only
            ;; checked when free to switch AND either no current position
            ;; or nearest-position would return nil — this prevents
            ;; card-under from hijacking when you're transitioning away
            ;; from a gap position and happen to cross over a card.
            nearest-gap (when (and filtered hit-point cursor-moved-enough?)
                          (find-nearest-position {:positions filtered
                                                  :svg-point hit-point}))
            card-under (when (and enable-card-under?
                                  graph view-layouts hit-point
                                  cursor-moved-enough?
                                  (nil? nearest-gap))
                         (find-card-under-point {:svg-point hit-point
                                                 :view-layouts view-layouts
                                                 :graph graph
                                                 :dragged-node-id dragged-node-id}))
            nearest (if (and current-pos (not cursor-moved-enough?))
                      ;; Locked in — keep current position
                      current-pos
                      ;; Nearest gap takes priority, card-under as fallback
                      (or nearest-gap card-under))
            ;; Nil-hold: absorb single-frame oscillation caused by layout shift.
            ;; When the phantom is placed, the layout shifts, pushing candidate
            ;; points past max-snap-distance. The phantom disappears, layout
            ;; snaps back, and next frame the position is found again. We hold
            ;; the current position for one frame when nearest goes nil. If the
            ;; NEXT frame also returns nil, we release (genuinely far away).
            ;; Only applies to position→nil transitions.
            was-holding? (:nil-hold? state)
            hovered (cond
                      ;; Found a position — use it, clear any hold
                      (some? nearest)
                      nearest

                      ;; nearest is nil, we had a position, not already holding → hold
                      (and (nil? nearest) current-pos (not was-holding?))
                      current-pos

                      ;; nearest is nil, already held for one frame → release
                      :else
                      nil)
            next-nil-hold? (and (nil? nearest) (some? hovered))
            prev-id (:position-id current-pos)
            next-id (:position-id hovered)
            switched? (not= prev-id next-id)
            ;; Only reset lock-in when the phantom moves to a different
            ;; part of the tree. Switching between e.g. insert-before-X
            ;; and insert-after-X keeps the phantom among the same siblings
            ;; so the lock-in stays. But reparent-onto-X → insert-before-X
            ;; moves the phantom from child-of-X to sibling-of-X, so we reset.
            same-phantom-parent? (and current-pos hovered graph
                                      (= (phantom-parent-id {:action (:action current-pos) :graph graph})
                                         (phantom-parent-id {:action (:action hovered) :graph graph})))
            reset-lock-in? (and switched? (not same-phantom-parent?))]
        {:state (assoc state
                  :current-mouse-pos screen-pos
                  :hovered-position hovered
                  :nil-hold? next-nil-hold?
                  :shift-held? (boolean shift-held?)
                  ;; Update lock-in point when phantom moves to a different tree parent
                  :lock-in-svg-point (if reset-lock-in?
                                       svg-point
                                       lock-in)
                  ;; Store safe zone for debug overlay; clear when phantom parent changes
                  :safe-zone-polygon (if reset-lock-in? nil safe-zone))})

      [:dragging :mouse-up]
      (let [{:keys [graph keep-descendants?]} event-data
            hovered (:hovered-position state)]
        (if hovered
          (if (= (:drag-source state) :sidebar)
            (do
              (console :debug "[drag] sidebar drop on position:" (:position-id hovered))
              {:state initial-state
               :effects [:drag-ended :stop-tracking
                         [:commit-sidebar-drop {:graph graph
                                                :action (:action hovered)
                                                :sidebar-drag-data (:sidebar-drag-data state)}]]})
            (do
              (console :debug "[drag] drop on position:" (:position-id hovered)
                       (when keep-descendants? "(with descendants)"))
              (let [new-graph (apply-drop-action
                                {:graph graph
                                 :dragged-node-id (:dragged-node-id state)
                                 :action (:action hovered)
                                 :keep-descendants? keep-descendants?})]
                {:state initial-state
                 :effects [:drag-ended :stop-tracking [:commit-graph new-graph]]})))
          (do
            (console :debug "[drag] drop on nothing, cancelling")
            {:state initial-state
             :effects [:drag-ended :stop-tracking]})))

      ;; Shift key press/release during drag — update state for tooltip + descendant fading
      [:dragging :shift-change]
      (let [{:keys [shift-held?]} event-data]
        {:state (assoc state :shift-held? shift-held?)})

      ;; No-op in pending phase to avoid "unhandled" log
      [:pending :shift-change]
      {:state state}

      (do
        (console :debug "[drag] unhandled:" phase event-type)
        {:state state}))))
